import { Link } from "react-router-dom";
import {
  ArrowRight, Baby, Banknote, Car, Check, HeartHandshake, Home as HomeIcon,
  MessagesSquare, Phone, ShieldCheck, ShoppingCart, Users, Zap,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import { ShieldBuilder } from "../components/ShieldBuilder";
import { ScheduleSection } from "../components/ScheduleSection";
import { PHONE, PHONE_TEL, postChecklist, track, usePageMeta } from "../lib/api";
import { useRef, useState } from "react";
import { toast } from "sonner";

const TRUST = [
  { icon: ShieldCheck, title: "Real Protection", text: "Coverage designed for life's financial surprises." },
  { icon: Users, title: "Real People", text: "Straightforward help from an independent insurance brokerage." },
  { icon: MessagesSquare, title: "Real Answers", text: "Understand the options before making a decision." },
  { icon: HeartHandshake, title: "Real Relationships", text: "Protection shouldn't end when the policy begins." },
];

const GAPS = [
  { icon: HomeIcon, label: "Home", sub: "Mortgage & rent" },
  { icon: Banknote, label: "Income", sub: "Paychecks that pause" },
  { icon: ShoppingCart, label: "Food", sub: "Groceries & essentials" },
  { icon: Zap, label: "Utilities", sub: "Lights, heat, water" },
  { icon: Baby, label: "Childcare", sub: "Care while you recover" },
  { icon: Car, label: "Travel", sub: "Getting to treatment" },
];

const CHECKLIST_PREVIEW = [
  "If something happened tomorrow, what would my current coverage actually pay for?",
  "What are my deductibles, copays, and out-of-pocket maximums right now?",
  "If I couldn't work for 60 days, how would the bills get paid?",
  "Does my health insurance cover anything beyond medical bills?",
  "If I were diagnosed with a serious illness, what cash would my family have?",
  "Who depends on my income — and what would life look like without it?",
  "When did I last review my coverage with a real person, in plain language?",
];

const WHY = [
  "We explain before we recommend.",
  "We show the gaps — not manufacture fear.",
  "We believe insurance should make sense.",
  "We represent protection, not confusion.",
  "We build relationships, not transactions.",
  "We believe families deserve to understand what they're buying.",
];

const SHOW_US = [
  "Show us what it covers.",
  "Show us what it doesn't.",
  "Show us what happens when life changes.",
  "Show us our options.",
];

const ChecklistCapture = () => {
  const started = useRef(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ first_name: "", email: "", marketing_consent: false, company: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim()) {
      toast.error("Add your first name and email and we'll send it right over.");
      return;
    }
    setLoading(true);
    try {
      await postChecklist({ ...form, source: "homepage-checklist" });
      track("email_capture", { source: "checklist" });
      toast.success("Checklist sent — check your inbox.");
      setForm({ first_name: "", email: "", marketing_consent: false, company: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="checklist-download-form" className="space-y-4" noValidate>
      <input type="text" name="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div>
        <label htmlFor="checklist-name" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-primer">First name</label>
        <input id="checklist-name" data-testid="checklist-first-name" className="input-field" placeholder="First name" value={form.first_name} onChange={(e) => { if (!started.current) { started.current = true; } setForm({ ...form, first_name: e.target.value }); }} />
      </div>
      <div>
        <label htmlFor="checklist-email" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-primer">Email</label>
        <input id="checklist-email" data-testid="checklist-email" type="email" className="input-field" placeholder="you@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </div>
      <label className="flex items-start gap-3 text-xs leading-relaxed text-primer">
        <input type="checkbox" data-testid="checklist-consent" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="mt-0.5 h-4 w-4 shrink-0 accent-[#A8E600]" />
        It&apos;s okay to send me occasional helpful insurance information from Show Me Shield.
      </label>
      <button type="submit" data-testid="checklist-download-submit" disabled={loading} className="btn-lime w-full disabled:opacity-60">
        {loading ? "Sending..." : "Send Me the Checklist"} <ArrowRight className="h-4 w-4" />
      </button>
    </form>
  );
};

const Home = () => {
  usePageMeta(
    "Show Me Shield | Supplemental, Life & Health Insurance in Missouri",
    "Explore supplemental, life and health insurance options with Show Me Shield, a private independent Missouri insurance brokerage focused on clear answers and real conversations.",
    { path: "/" }
  );

  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden bg-ink grid-lines">
        <div className="pointer-events-none absolute -left-40 top-10 h-96 w-96 rounded-full bg-lime/10 blur-[140px]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 pb-20 pt-14 sm:px-6 lg:grid-cols-2 lg:pb-28 lg:pt-20">
          <Reveal>
            <p className="eyebrow mb-3" data-testid="hero-eyebrow">Supplemental &bull; Life &bull; Health</p>
            <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500" data-testid="hero-broker-badge">
              Independent Insurance Brokerage &bull; Missouri
            </p>
            <h1 className="h-display text-4xl text-white sm:text-5xl lg:text-6xl">
              Don&apos;t just tell me I&apos;m covered. <span className="text-lime">Show me.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">
              Insurance doesn&apos;t have to be confusing. Show Me Shield is an independent Missouri
              insurance brokerage helping individuals and families understand their protection, explore
              supplemental, life and health insurance options, and prepare for the expenses life can
              leave behind.
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <Link to="/contact" data-testid="hero-primary-cta" className="btn-lime animate-glow-pulse">
                Talk With a Real Person <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#build-your-shield" data-testid="hero-secondary-cta" onClick={() => track("explore_protection_click")} className="btn-ghost">
                Explore Your Protection
              </a>
            </div>
            <p className="mt-5 text-sm text-zinc-500">
              No pressure. No obligation. Just a conversation about your options.
            </p>
            <a
              href={PHONE_TEL}
              data-testid="hero-phone-link"
              onClick={() => track("phone_click", { location: "hero" })}
              className="mt-6 inline-flex items-center gap-2 font-mono text-base font-bold text-white transition-colors hover:text-lime"
            >
              <Phone className="h-4 w-4 text-lime" /> {PHONE}
            </a>
          </Reveal>
          <Reveal delay={150} className="relative">
            <div className="relative overflow-hidden rounded-md border border-line lime-glow">
              <img
                src="/assets/brand/hero-office.webp"
                width="1000"
                height="1357"
                alt="A Show Me Shield broker in the office — real people, real conversations about your protection"
                className="h-full w-full object-cover object-top"
                loading="eager"
                fetchpriority="high"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-3 p-5">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">
                  Real Protection. Real Peace of Mind.
                </span>
                <span className="h-px flex-1 bg-lime/50" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-line bg-primer-dark/40">
        <div data-testid="trust-strip-container" className="mx-auto grid max-w-7xl grid-cols-1 gap-px sm:grid-cols-2 lg:grid-cols-4">
          {TRUST.map((t, i) => (
            <Reveal key={t.title} delay={i * 80} className="bg-ink">
              <div className="flex h-full items-start gap-4 px-6 py-7">
                <t.icon className="mt-0.5 h-6 w-6 shrink-0 text-lime" strokeWidth={1.75} />
                <div>
                  <h2 className="font-display text-base font-bold uppercase tracking-wider text-white">{t.title}</h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-zinc-400">{t.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* EDUCATIONAL GAP */}
      <section className="bg-paper grid-lines-light">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
          <Reveal className="max-w-3xl">
            <p className="eyebrow-dark mb-4">The Question Nobody Asks</p>
            <h2 className="h-display text-3xl text-ink sm:text-4xl lg:text-5xl">
              Health insurance may pay the doctor. What pays everything else?
            </h2>
            <p className="mt-5 text-base leading-relaxed text-primer sm:text-lg">
              A major medical event can create expenses far beyond the hospital bill — the mortgage,
              utilities, groceries, childcare, travel to treatment, and the income that pauses while
              you recover.
            </p>
          </Reveal>

          <div data-testid="educational-gap-visual" className="mt-14 grid items-center gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Reveal className="sm:col-span-2 lg:col-span-1 lg:row-span-2">
              <div className="flex h-full flex-col justify-center rounded-md border-2 border-ink bg-ink p-8 text-center">
                <ShieldCheck className="mx-auto h-8 w-8 text-lime" strokeWidth={1.75} />
                <h3 className="h-display mt-4 text-2xl text-white">Health Insurance</h3>
                <p className="mt-2 text-sm text-zinc-400">Helps pay the doctors and hospital for covered services.</p>
              </div>
            </Reveal>
            {GAPS.map((g, i) => (
              <Reveal key={g.label} delay={i * 60}>
                <div className="card-light flex items-center gap-4 p-5">
                  <g.icon className="h-6 w-6 shrink-0 text-primer" strokeWidth={1.75} />
                  <div>
                    <h3 className="font-display text-base font-bold uppercase tracking-wide text-ink">{g.label}</h3>
                    <p className="text-sm text-primer">{g.sub}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-14 max-w-3xl">
            <h3 className="h-display text-2xl text-ink sm:text-3xl">
              Your life doesn&apos;t stop because you got sick.
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-primer sm:text-base">
              Depending on the policy and covered event, supplemental insurance benefits may provide
              cash benefits that can help with expenses during difficult situations. We&apos;ll show
              you exactly how — and where your current coverage stands.
            </p>
            <a href="#build-your-shield" data-testid="gap-cta" className="btn-lime mt-7">
              Show Me My Options <ArrowRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* BUILD YOUR SHIELD */}
      <section id="build-your-shield" className="relative overflow-hidden border-y border-line bg-ink grid-lines scroll-mt-20">
        <div className="pointer-events-none absolute -right-40 top-0 h-96 w-96 rounded-full bg-lime/10 blur-[140px]" />
        <div className="relative mx-auto max-w-4xl px-4 py-20 sm:px-6 sm:py-24">
          <Reveal className="text-center">
            <p className="eyebrow mb-4">Interactive</p>
            <h2 className="h-display text-3xl text-white sm:text-4xl lg:text-5xl">Build Your Shield</h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-zinc-400">
              Answer one simple question. See the areas worth reviewing. Then decide if a conversation
              makes sense. That&apos;s it.
            </p>
          </Reveal>
          <Reveal delay={60} className="mt-8 text-center">
            <p data-testid="builder-trust-preamble" className="font-display text-lg font-bold uppercase tracking-wider text-white">
              No pressure. <span className="text-lime">No obligation.</span> No insurance dictionary required.
            </p>
            <p className="mt-2 text-sm text-zinc-400">
              Tell us what you&apos;re trying to protect. We&apos;ll start with a conversation.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {["Independent Brokerage", "Missouri-Focused Guidance", "Real Human Conversations"].map((t) => (
                <span key={t} className="rounded-full border border-line bg-ink-2 px-4 py-2 text-xs font-medium text-zinc-300">
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120} className="mt-10">
            <ShieldBuilder />
          </Reveal>
          <p className="mt-8 text-center text-sm text-zinc-500">
            Prefer to talk first? No pressure. No obligation.{" "}
            <a
              href={PHONE_TEL}
              data-testid="builder-phone-link"
              onClick={() => track("phone_click", { location: "builder" })}
              className="font-mono font-bold text-lime"
            >
              Call {PHONE}
            </a>
          </p>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="bg-paper-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
          <Reveal className="max-w-3xl">
            <p className="eyebrow-dark mb-4">What We Help With</p>
            <h2 className="h-display text-3xl text-ink sm:text-4xl lg:text-5xl">
              Protection you can actually understand.
            </h2>
          </Reveal>
          <div data-testid="services-preview-grid" className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              {
                title: "Supplemental Insurance",
                text: "Protection beyond major medical insurance — accident, critical illness, cancer, heart/stroke and hospital indemnity coverage.",
                to: "/services#supplemental",
              },
              {
                title: "Life Insurance",
                text: "Protect the people who depend on you — term, permanent options where offered, and final-expense solutions where appropriate.",
                to: "/services#life",
              },
              {
                title: "Health Insurance",
                text: "Individual and family health coverage that should make sense before you need it. We'll walk through it together.",
                to: "/services#health",
              },
            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 90}>
                <Link to={s.to} data-testid={`service-preview-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="card-light group flex h-full flex-col p-7">
                  <h3 className="font-display text-xl font-bold uppercase tracking-wide text-ink group-hover:text-ink">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-primer">{s.text}</p>
                  <span className="mt-5 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-ink transition-colors group-hover:text-lime-dim">
                    See what it covers <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY SHOW ME SHIELD */}
      <section className="bg-ink">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <Reveal>
              <p className="eyebrow mb-4">Why Show Me Shield</p>
              <h2 className="h-display text-3xl text-white sm:text-4xl lg:text-5xl">
                Don&apos;t take our word for it. <span className="text-lime">Let us show you.</span>
              </h2>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-zinc-400">
                You probably know what your premium costs. Do you know what your protection looks like?
                Let&apos;s look at the whole picture — together.
              </p>
              <Link to="/about" className="btn-ghost mt-8">Our Story <ArrowRight className="h-4 w-4" /></Link>
            </Reveal>
            <div>
              {WHY.map((w, i) => (
                <Reveal key={w} delay={i * 60}>
                  <div className="flex items-center gap-4 border-b border-line py-4">
                    <Check className="h-5 w-5 shrink-0 text-lime" strokeWidth={2.5} />
                    <p className="font-display text-lg font-semibold uppercase tracking-wide text-zinc-100">{w}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MISSOURI SERVICE AREA */}
      <section className="border-y border-line bg-ink-2" aria-labelledby="missouri-heading">
        <div className="mx-auto max-w-4xl px-4 py-20 sm:px-6 sm:py-28">
          <Reveal>
            <p className="eyebrow mb-4">Service Area</p>
            <h2 id="missouri-heading" className="h-display text-3xl text-white sm:text-4xl lg:text-5xl">
              Proudly serving Missouri.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-zinc-400">
              Show Me Shield was created around a very Missouri idea: don&apos;t just tell us — show us.
              As an independent insurance brokerage, we help people across the entire state understand
              their supplemental, life, and health insurance options — by phone and online, so no
              office visit is ever required.
            </p>
            <div className="mt-8 space-y-0">
              {SHOW_US.map((s, i) => (
                <Reveal key={s} delay={i * 70}>
                  <div className="flex items-center gap-4 border-b border-line py-3.5">
                    <span className="h-px w-8 bg-lime" />
                    <p className="font-display text-lg font-semibold uppercase tracking-wide text-white">{s}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="missouri-cities">
              {["Springfield", "Kansas City", "St. Louis", "Joplin", "Columbia", "Jefferson City", "Branson", "Lake of the Ozarks", "Cape Girardeau"].map((c) => (
                <span key={c} className="rounded-full border border-line bg-ink px-3.5 py-1.5 text-xs font-medium text-zinc-300">
                  {c}
                </span>
              ))}
            </div>
            <p className="mt-4 text-sm leading-relaxed text-zinc-500">
              Serving communities throughout Southwest Missouri, Central Missouri, Southeast Missouri,
              Northwest Missouri, Northeast Missouri, and surrounding areas.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PHONE HUMAN */}
      <section className="bg-paper">
        <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6">
          <Reveal>
            <p className="eyebrow-dark mb-4">Rather Talk to a Human?</p>
            <h2 className="h-display text-3xl text-ink sm:text-4xl lg:text-5xl">Good. So would we.</h2>
            <p className="mx-auto mt-4 max-w-lg text-base text-primer">
              Call Show Me Shield and tell us what&apos;s on your mind.
            </p>
            <a
              href={PHONE_TEL}
              data-testid="human-phone-cta"
              onClick={() => track("phone_click", { location: "human_section" })}
              className="mt-8 inline-flex items-center gap-3 font-mono text-2xl font-bold text-ink transition-colors hover:text-lime-dim sm:text-3xl"
            >
              <Phone className="h-7 w-7 text-lime-dim" /> {PHONE}
            </a>
            <p className="mt-4 text-sm text-primer">No obligation. Start with a conversation.</p>
          </Reveal>
        </div>
      </section>

      {/* SCHEDULE A QUICK CONSULTATION */}
      <ScheduleSection />

      {/* CHECKLIST LEAD MAGNET */}
      <section className="border-t border-ink/10 bg-paper-muted grid-lines-light">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 py-20 sm:px-6 sm:py-28 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow-dark mb-4">Free Download</p>
            <h2 className="h-display text-3xl text-ink sm:text-4xl">
              Get the Show Me Shield checklist.
            </h2>
            <p className="mt-4 text-base font-semibold text-ink">
              7 Questions to Ask Before You Assume You&apos;re Fully Protected
            </p>
            <ol className="mt-6 space-y-3">
              {CHECKLIST_PREVIEW.map((q, i) => (
                <li key={i} className="flex gap-3 text-sm leading-relaxed text-primer">
                  <span className="font-mono text-xs font-bold text-lime-dim">{String(i + 1).padStart(2, "0")}</span>
                  {q}
                </li>
              ))}
            </ol>
          </Reveal>
          <Reveal delay={120}>
            <div className="card-light !p-7 sm:!p-9">
              <h3 className="h-display text-xl text-ink">Where should we send it?</h3>
              <p className="mb-6 mt-2 text-sm text-primer">Instant delivery. No spam. No pressure.</p>
              <ChecklistCapture />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
